import { Link } from "react-router-dom";
import LegalPage, { Mail, OPERATOR, type LegalSection } from "../components/LegalPage";

const SECTIONS: LegalSection[] = [
  {
    heading: "Agreement",
    body: (
      <p>
        These Terms of Service are an agreement between you and {OPERATOR} ("we", "us") for your use of Set &amp;
        Ledger at www.setandledger.com (the "Service"). By creating an account or using the Service, you agree to these
        Terms. If you use the Service for a business, you confirm you're authorized to accept these Terms for it.
      </p>
    ),
  },
  {
    heading: "The Service",
    body: (
      <p>
        Set &amp; Ledger imports financial emails and files you upload, extracts receipts, bills and income with AI,
        and helps you organize, review and report on them, and optionally send them to QuickBooks Online. We may add,
        change or remove features over time.
      </p>
    ),
  },
  {
    heading: "Your account",
    body: (
      <ul>
        <li>You must be at least 18 years old and give accurate information when you sign up.</li>
        <li>Keep your password secure. You're responsible for activity on your account.</li>
        <li>
          Tell us right away at <Mail /> if you think someone has used your account without permission.
        </li>
      </ul>
    ),
  },
  {
    heading: "Your data",
    body: (
      <>
        <p>
          You own the emails, files and records in your account ("Your Data"). You give us permission to host, copy
          and process Your Data, including sending it to the AI and integration providers described in our{" "}
          <Link to="/privacy">Privacy Policy</Link>, only as needed to provide the Service to you.
        </p>
        <p>
          You confirm that you have the right to connect each mailbox you connect and to let us process the messages in
          it, including any personal information about other people they contain.
        </p>
      </>
    ),
  },
  {
    heading: "AI results and no professional advice",
    body: (
      <p>
        Extraction, duplicate detection, vendor matching and review flags are automated and can be wrong or incomplete.
        You're responsible for reviewing records before relying on them or sending them to QuickBooks, and for your own
        books and tax filings. Set &amp; Ledger doesn't provide accounting, tax, legal or financial advice.
      </p>
    ),
  },
  {
    heading: "Third-party services",
    body: (
      <p>
        The Service connects to services run by others, such as Gmail, Outlook, your mail provider and Intuit
        QuickBooks Online. Your use of them is governed by their terms, and we aren't responsible for them or for
        changes or outages on their side. When you sync, we create transactions in your QuickBooks company on your
        instructions.
      </p>
    ),
  },
  {
    heading: "Acceptable use",
    body: (
      <>
        <p>You agree not to:</p>
        <ul>
          <li>use the Service for anything illegal or to process data you have no right to;</li>
          <li>connect a mailbox or accounting company you aren't authorized to access;</li>
          <li>
            interfere with the Service, probe its security, overload it, or reverse-engineer it except where the law
            allows;
          </li>
          <li>upload malware, or resell or provide the Service to others without our written permission.</li>
        </ul>
      </>
    ),
  },
  {
    heading: "Fees",
    body: (
      <p>
        If you choose a paid plan, you'll see the price and terms before you're charged. We'll give you at least 30
        days' notice before changing the price of a plan you're on.
      </p>
    ),
  },
  {
    heading: "Privacy",
    body: (
      <p>
        Our <Link to="/privacy">Privacy Policy</Link> explains how we handle personal information, and is part of these
        Terms.
      </p>
    ),
  },
  {
    heading: "Suspension and termination",
    body: (
      <p>
        You can stop using the Service at any time and ask us to delete your account. We may suspend or end your access
        if you breach these Terms, if your use puts the Service or others at risk, or if the law requires it. Where
        practical, we'll tell you first. After termination we delete Your Data as described in the Privacy Policy.
        Sections that by their nature should continue (such as disclaimers, limits of liability and governing law)
        survive termination.
      </p>
    ),
  },
  {
    heading: "Disclaimers",
    body: (
      <p>
        To the extent the law allows, the Service is provided "as is" and "as available", without warranties of any
        kind, express or implied, including that it will be accurate, uninterrupted or error-free, or fit for a
        particular purpose.
      </p>
    ),
  },
  {
    heading: "Limitation of liability",
    body: (
      <p>
        To the extent the law allows, we aren't liable for indirect, incidental, special or consequential damages, or
        for lost profits, lost data, or tax penalties and interest, arising from your use of the Service. Our total
        liability for any claim relating to the Service is limited to the greater of the amount you paid us for the
        Service in the 12 months before the claim and CAD $100. Some jurisdictions don't allow these limits, so they
        may not all apply to you.
      </p>
    ),
  },
  {
    heading: "Indemnity",
    body: (
      <p>
        You agree to compensate us for claims, losses and costs (including reasonable legal fees) arising from Your
        Data, your breach of these Terms, or your connecting accounts you weren't authorized to connect.
      </p>
    ),
  },
  {
    heading: "Changes to these Terms",
    body: (
      <p>
        We may update these Terms. We'll post the new version here with a new effective date and, for significant
        changes, tell you by email or in the app before they take effect. If you keep using the Service after that, the
        new Terms apply.
      </p>
    ),
  },
  {
    heading: "Governing law",
    body: (
      <p>
        These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada that apply
        there. The courts of Ontario have exclusive jurisdiction over any dispute, except where the law of your place
        of residence gives you rights that can't be waived.
      </p>
    ),
  },
  {
    heading: "General",
    body: (
      <p>
        These Terms and the Privacy Policy are the whole agreement between you and us about the Service. If any part is
        found unenforceable, the rest still applies. Not enforcing a provision isn't a waiver of it. You may not
        transfer these Terms without our consent; we may transfer them as part of a merger or sale of the business.
        Questions: <Mail />.
      </p>
    ),
  },
];

export default function Terms() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms of Service"
      intro={
        <p>
          Please read these Terms carefully. They explain your rights and responsibilities when you use Set &amp;
          Ledger.
        </p>
      }
      sections={SECTIONS}
    />
  );
}
